import { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, Send, Package2, Mail, Phone, MapPin, Menu, X, Share2, Star } from 'lucide-react';
import CurrencySymbol from '@/components/CurrencySymbol';
import { formatCurrency, fmtNum } from '@/utils/currency';

export default function PublicWebsite() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user, logout } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [productStats, setProductStats] = useState({});
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingProduct, setSharingProduct] = useState(null);
  const [showMessagingChoice, setShowMessagingChoice] = useState(false);
  const [showViberMessage, setShowViberMessage] = useState(false);
  const [orderMessage, setOrderMessage] = useState('');
  const [deliveryType, setDeliveryType] = useState('land'); // 'land' or 'launch'
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash' or 'bank_transfer'
  const [exchangeRate, setExchangeRate] = useState(1);
  const [customerCurrency, setCustomerCurrency] = useState('USD');
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('landmark'); // 'landmark' or 'supply_boat'
  const [deliveryDetails, setDeliveryDetails] = useState({
    // Landmark fields
    first_name: '',
    last_name: '',
    contact_number: '',
    designation: '',
    street_address: '',
    building_name: '',
    floor: '',
    google_map_link: '',
    // Supply Boat fields
    captain_full_name: '',
    captain_id_no: '',
    dhoni_name: '',
    jetty: '',
    captain_number: ''
  });
  const [orderForm, setOrderForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });
  const [content, setContent] = useState(() => {
    // Try to load from localStorage first for instant display
    const cached = localStorage.getItem('websiteContent');
    return cached ? JSON.parse(cached) : {
      landing: {},
      about: {},
      contact: {},
      footer: {},
      settings: {}
    };
  });
  const [logo, setLogo] = useState(() => {
    // Try to load from localStorage first
    return localStorage.getItem('websiteLogo') || null;
  });
  const [companyName, setCompanyName] = useState(() => {
    return localStorage.getItem('companyName') || 'ASSETCO';
  });
  const [logoSize, setLogoSize] = useState(() => {
    return parseInt(localStorage.getItem('websiteLogoSize')) || 96;
  });
  const [showCompanyName, setShowCompanyName] = useState(() => {
    const stored = localStorage.getItem('showCompanyName');
    return stored === null ? true : stored === 'true';
  });
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [inquiryForm, setInquiryForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    message: ''
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState({});
  const [countdown, setCountdown] = useState({});

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchContent();
    fetchLogo();
    fetchExchangeRate();
    
    // Set customer currency if logged in
    if (user && user.user_type === 'customer') {
      setCustomerCurrency(user.currency_preference || 'USD');
    }
  }, [user]);

  const fetchExchangeRate = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/currency`);
      if (response.data && response.data.usd_to_mvr_rate) {
        setExchangeRate(response.data.usd_to_mvr_rate);
      }
    } catch (error) {
      console.error('Failed to fetch exchange rate');
      setExchangeRate(15.42); // Default rate
    }
  };

  // Helper function to convert price to customer currency
  const convertPrice = (usdPrice) => {
    if (customerCurrency === 'MVR') {
      return usdPrice * exchangeRate;
    }
    return usdPrice;
  };

  // Helper function to format price with currency symbol
  const formatPrice = (usdPrice) => {
    const price = convertPrice(usdPrice);
    return customerCurrency === 'MVR' 
      ? `${fmtNum(price)} ރ.` 
      : `$${fmtNum(price)}`;
  };

  // Component to display price with proper MVR symbol
  const PriceDisplay = ({ usdPrice, className = "" }) => {
    const price = convertPrice(usdPrice);
    if (customerCurrency === 'MVR') {
      return (
        <span className={className}>
          <CurrencySymbol currency="MVR" width={14} height={14} /> {fmtNum(price)}
        </span>
      );
    }
    return <span className={className}>${fmtNum(price)}</span>;
  };

  // Refetch logo and content whenever location changes (navigation)
  useEffect(() => {
    // Fetch fresh data - timestamp will update if content changed
    fetchLogo();
    fetchContent();
  }, [location.pathname]);

  // Refetch logo and content when page becomes visible (for when user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchLogo();
        fetchContent();
      }
    };

    const handleFocus = () => {
      fetchLogo();
      fetchContent();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const adBarsWithCountdown = content.landing.ad_bars?.filter(
      bar => bar.enabled && bar.countdown_enabled && bar.countdown_end_date
    ) || [];

    if (adBarsWithCountdown.length === 0) return;

    const interval = setInterval(() => {
      const newCountdowns = {};
      adBarsWithCountdown.forEach(bar => {
        const endDate = new Date(bar.countdown_end_date).getTime();
        const now = new Date().getTime();
        const distance = endDate - now;

        if (distance > 0) {
          const days = Math.floor(distance / (1000 * 60 * 60 * 24));
          const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);
          newCountdowns[bar.id] = { days, hours, minutes, seconds };
        } else {
          newCountdowns[bar.id] = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
      });
      setCountdown(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [content.landing.ad_bars]);

  // Carousel auto-slide effect
  useEffect(() => {
    const adBarsWithCarousel = content.landing.ad_bars?.filter(
      bar => bar.enabled && bar.carousel_enabled && bar.carousel_images?.length > 1
    ) || [];

    if (adBarsWithCarousel.length === 0) return;

    const intervals = adBarsWithCarousel.map(bar => {
      return setInterval(() => {
        setCurrentSlide(prev => ({
          ...prev,
          [bar.id]: ((prev[bar.id] || 0) + 1) % bar.carousel_images.length
        }));
      }, 3000); // Change slide every 3 seconds
    });

    return () => intervals.forEach(interval => clearInterval(interval));
  }, [content.landing.ad_bars]);

  useEffect(() => {
    // Handle announcement bar timeout
    if (content.landing.announcement_bar?.enabled && content.landing.announcement_bar.timeout > 0) {
      const timer = setTimeout(() => {
        setShowAnnouncement(false);
      }, content.landing.announcement_bar.timeout * 1000);
      return () => clearTimeout(timer);
    }
  }, [content.landing.announcement_bar]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/public/products`);
      setProducts(response.data);
      
      // Fetch stats for all products
      fetchAllProductStats(response.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  const fetchAllProductStats = async (productsList) => {
    try {
      const statsPromises = productsList.map(product =>
        axios.get(`${API}/products/${product.id}/stats`)
          .then(res => ({ productId: product.id, stats: res.data }))
          .catch(() => ({ productId: product.id, stats: null }))
      );
      
      const allStats = await Promise.all(statsPromises);
      
      const statsMap = {};
      allStats.forEach(({ productId, stats }) => {
        if (stats) {
          statsMap[productId] = stats;
        }
      });
      
      setProductStats(statsMap);
    } catch (error) {
      console.error('Failed to load product stats');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/public/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchContent = async () => {
    try {
      const pages = ['landing', 'about', 'contact', 'footer', 'settings'];
      const promises = pages.map(page => axios.get(`${API}/website/content/${page}`));
      const responses = await Promise.all(promises);
      
      const newContent = {};
      responses.forEach((response, index) => {
        newContent[pages[index]] = response.data.content || {};
      });
      setContent(newContent);
      
      // Apply favicon dynamically if available
      if (newContent.settings?.favicon) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.rel = 'icon';
        link.href = newContent.settings.favicon;
        if (!document.querySelector("link[rel~='icon']")) {
          document.head.appendChild(link);
        }
      }
      
      // Cache in localStorage for instant loading on next visit
      localStorage.setItem('websiteContent', JSON.stringify(newContent));
      setIsLoadingContent(false);
    } catch (error) {
      console.error('Failed to load content');
      setIsLoadingContent(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/public/settings/company`);
      if (response.data.website_logo) {
        setLogo(response.data.website_logo);
        localStorage.setItem('websiteLogo', response.data.website_logo);
      }
      if (response.data.company_name) {
        setCompanyName(response.data.company_name);
        localStorage.setItem('companyName', response.data.company_name);
      }
      if (response.data.website_logo_size) {
        setLogoSize(response.data.website_logo_size);
        localStorage.setItem('websiteLogoSize', response.data.website_logo_size);
      }
      if (response.data.show_company_name !== undefined) {
        setShowCompanyName(response.data.show_company_name);
        localStorage.setItem('showCompanyName', response.data.show_company_name);
      }
    } catch (error) {
      console.error('Failed to load logo');
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { product_id: product.id, quantity: 1, product }]);
    }
    toast.success('Added to cart');
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const submitInquiry = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      toast.error('Please add items to cart');
      return;
    }

    try {
      await axios.post(`${API}/public/inquiry`, {
        customer_name: inquiryForm.customer_name,
        customer_email: inquiryForm.customer_email,
        customer_phone: inquiryForm.customer_phone,
        message: inquiryForm.message,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }))
      });

      toast.success('Inquiry submitted successfully! We will contact you soon.');
      setCart([]);
      setShowInquiryForm(false);
      setInquiryForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry');
    }
  };

  const openViberWithOrder = async () => {
    try {
      // Copy to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(orderMessage);
      }
      
      // Get Viber link from settings
      const settingsResponse = await axios.get(`${API}/public/settings/company`);
      const storePhone = settingsResponse.data.store_handler_phone || '+960 7614441';
      
      let viberUrl;
      if (settingsResponse.data.viber_link && settingsResponse.data.viber_link.trim() !== '') {
        viberUrl = settingsResponse.data.viber_link;
      } else {
        const phoneNumber = storePhone.replace(/[^0-9]/g, '');
        const formattedPhone = storePhone.startsWith('+') ? storePhone : `+${phoneNumber}`;
        viberUrl = `viber://contact?number=${encodeURIComponent(formattedPhone)}`;
      }
      
      // Open Viber
      window.location.href = viberUrl;
      
      toast.success('Opening Viber... Paste the message and send!', {
        duration: 5000
      });
      
      // Close dialog and clear form
      setShowViberMessage(false);
      setCart([]);
      setDeliveryType('land');
      setPaymentMethod('cash');
      setOrderForm({
        customer_name: '',
        customer_email: '',
        customer_phone: ''
      });
    } catch (error) {
      console.error('Error opening Viber:', error);
      toast.error('Failed to open Viber');
    }
  };

  const handleBuyNow = (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      toast.error('Please add items to cart');
      return;
    }

    // For logged-in customers, show delivery details dialog
    if (user && user.user_type === 'customer') {
      setShowCart(false);
      setShowDeliveryDialog(true);
      return;
    }

    // For non-logged-in users, prompt them to login/register
    toast.info('Please login or create an account to checkout');
    setShowCart(false);
    navigate('/login');
  };

  const createCustomerOrder = async () => {
    try {
      // Validate delivery details based on method
      if (deliveryMethod === 'landmark') {
        if (!deliveryDetails.first_name || !deliveryDetails.last_name || 
            !deliveryDetails.contact_number || !deliveryDetails.designation) {
          toast.error('Please fill all required fields');
          return;
        }
        // Validate contact number
        if (deliveryDetails.contact_number.length !== 7 || 
            (!deliveryDetails.contact_number.startsWith('7') && !deliveryDetails.contact_number.startsWith('9'))) {
          toast.error('⚠️ Contact number must be 7 digits and start with 7 or 9');
          return;
        }
      } else if (deliveryMethod === 'supply_boat') {
        if (!deliveryDetails.captain_full_name || !deliveryDetails.captain_id_no || 
            !deliveryDetails.dhoni_name || !deliveryDetails.jetty || !deliveryDetails.captain_number) {
          toast.error('Please fill all required fields');
          return;
        }
        // Validate captain number
        if (deliveryDetails.captain_number.length !== 7 || 
            (!deliveryDetails.captain_number.startsWith('7') && !deliveryDetails.captain_number.startsWith('9'))) {
          toast.error('⚠️ Captain number must be 7 digits and start with 7 or 9');
          return;
        }
      }

      const orderItems = cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product.name,
        quantity: item.quantity,
        price: convertPrice(item.product.price),
        currency: customerCurrency
      }));

      // Prepare delivery details based on method
      const orderDeliveryDetails = {
        method: deliveryMethod,
        ...(deliveryMethod === 'landmark' ? {
          first_name: deliveryDetails.first_name,
          last_name: deliveryDetails.last_name,
          contact_number: deliveryDetails.contact_number,
          designation: deliveryDetails.designation,
          street_address: deliveryDetails.street_address || '',
          building_name: deliveryDetails.building_name || '',
          floor: deliveryDetails.floor || '',
          google_map_link: deliveryDetails.google_map_link || null
        } : {
          captain_full_name: deliveryDetails.captain_full_name,
          captain_id_no: deliveryDetails.captain_id_no,
          dhoni_name: deliveryDetails.dhoni_name,
          jetty: deliveryDetails.jetty,
          captain_number: deliveryDetails.captain_number
        })
      };

      const response = await axios.post(`${API}/customer/orders`, {
        items: orderItems,
        notes: null,
        delivery_details: orderDeliveryDetails
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Check if PO verification is required (Tier 1 customers)
      if (response.data.requires_po_verification) {
        toast.success(
          `Order ${response.data.order_number} created! Please add PO Number and PO Document in your dashboard.`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Order ${response.data.order_number} created successfully!`);
      }
      
      setCart([]);
      setShowDeliveryDialog(false);
      // Reset delivery form
      setDeliveryDetails({
        first_name: '',
        last_name: '',
        contact_number: '',
        designation: '',
        street_address: '',
        building_name: '',
        floor: '',
        google_map_link: '',
        captain_full_name: '',
        captain_id_no: '',
        dhoni_name: '',
        jetty: '',
        captain_number: ''
      });
      setDeliveryMethod('landmark');
      
      // Always redirect to customer dashboard
      navigate('/customer-dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create order. Please try again.';
      
      // If error is about proof document, show specific message and redirect
      if (errorMsg.includes('Proof of Role')) {
        toast.error(errorMsg);
        setShowDeliveryDialog(false);
        navigate('/customer-dashboard'); // Redirect to dashboard where they can upload proof
      } else {
        toast.error(errorMsg);
      }
      console.error(error);
    }
  };

  const placeOrder = async (platform) => {
    try {
      // Calculate totals
      const subtotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

      // Create order
      const response = await axios.post(`${API}/orders`, {
        customer_name: orderForm.customer_name,
        customer_email: orderForm.customer_email,
        customer_phone: orderForm.customer_phone,
        payment_method: paymentMethod,
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
          total: item.product.price * item.quantity
        })),
        subtotal: subtotal,
        discount: 0,
        tax: 0,
        total: subtotal,
        delivery_type: deliveryType,
        ...(deliveryType === 'land' ? {
          delivery_address: orderForm.delivery_address,
          receiver_number: orderForm.receiver_number,
          road: orderForm.road,
          google_map_link: orderForm.google_map_link
        } : {
          package_info: orderForm.package_info,
          launch_name: orderForm.launch_name,
          captain_number: orderForm.captain_number,
          launch_arrival_date: orderForm.launch_arrival_date,
          launch_arrival_time: orderForm.launch_arrival_time,
          launch_departure_date: orderForm.launch_departure_date,
          launch_departure_time: orderForm.launch_departure_time,
          launch_departure_location: orderForm.launch_departure_location
        })
      });

      const order = response.data;

      // Get store handler phone from settings
      const settingsResponse = await axios.get(`${API}/public/settings/company`);
      const storePhone = settingsResponse.data.store_handler_phone || '+960 7614441';

      // Generate order message
      const itemsList = cart.map(item => 
        `${item.product.name} x ${item.quantity} - ${formatPrice(item.product.price * item.quantity)}`
      ).join('\n');

      // Build delivery information message
      let deliveryInfo = '';
      if (deliveryType === 'land') {
        deliveryInfo = `\n*Delivery Type:* Land\n` +
          `*Delivery Address:* ${orderForm.delivery_address}\n` +
          `*Receiver Number:* ${orderForm.receiver_number}\n` +
          `*Road:* ${orderForm.road}\n` +
          `${orderForm.google_map_link ? `*Google Map:* ${orderForm.google_map_link}\n` : ''}`;
      } else if (deliveryType === 'launch') {
        deliveryInfo = `\n*Delivery Type:* Launch/Boat\n` +
          `*Package Info:* ${orderForm.package_info}\n` +
          `*Launch Name:* ${orderForm.launch_name}\n` +
          `*Captain Number:* ${orderForm.captain_number}\n` +
          `*Arrival Date:* ${orderForm.launch_arrival_date}\n` +
          `*Arrival Time:* ${orderForm.launch_arrival_time}\n` +
          `*Departure Date:* ${orderForm.launch_departure_date}\n` +
          `*Departure Time:* ${orderForm.launch_departure_time}\n` +
          `*Departure Location:* ${orderForm.launch_departure_location}\n`;
      }

      const message = `*New Order - ${order.order_number}*\n\n` +
        `*Customer:* ${orderForm.customer_name}\n` +
        `*Phone:* ${orderForm.customer_phone}\n` +
        `${orderForm.customer_email ? `*Email:* ${orderForm.customer_email}\n` : ''}` +
        `*Payment Method:* ${paymentMethod === 'cash' ? 'Cash' : 'Bank Transfer'}\n` +
        deliveryInfo +
        `\n*Items:*\n${itemsList}\n\n` +
        `*Total:* ${formatCurrency(order.total)}`;

      // Open WhatsApp or Viber with message
      const encodedMessage = encodeURIComponent(message);
      const phoneNumber = storePhone.replace(/[^0-9]/g, ''); // Remove non-numeric characters

      if (platform === 'whatsapp') {
        toast.success('Order placed! Opening WhatsApp...');
        window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
        
        // Clear form and cart
        setCart([]);
        setShowMessagingChoice(false);
        setDeliveryType('land');
        setPaymentMethod('cash');
        setOrderForm({
          customer_name: '',
          customer_email: '',
          customer_phone: ''
        });
      } else if (platform === 'viber') {
        // Store message and show it to user before opening Viber
        setOrderMessage(message);
        setShowMessagingChoice(false);
        setShowViberMessage(true);
        return; // Don't clear form yet
        
        // Clear form and cart
        setCart([]);
        setShowMessagingChoice(false);
        setDeliveryType('land');
        setPaymentMethod('cash');
        setOrderForm({
          customer_name: '',
          customer_email: '',
          customer_phone: ''
        });
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    }
  };

  const handleShare = (product, platform) => {
    const productUrl = `${window.location.origin}/product/${product.id}`;
    const message = `Check out this product: ${product.name}\nPrice: ${formatPrice(product.price)}\n${productUrl}`;
    
    if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    } else if (platform === 'viber') {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(message);
        toast.info('Product info copied! Open Viber to share.');
      }
      window.open('viber://forward?text=' + encodeURIComponent(message), '_blank');
    } else if (platform === 'copy') {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(productUrl);
        toast.success('Product link copied to clipboard!');
      }
    }
    setShowShareDialog(false);
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category_id === selectedCategory);

  const cartTotal = cart.reduce((sum, item) => sum + (convertPrice(item.product.price) * item.quantity), 0);

  return (
    <>
      <Helmet>
        <title>{content.settings?.page_title || 'ASSETCO - Your Business Partner'}</title>
        {content.settings?.favicon && (
          <link rel="icon" href={content.settings.favicon} />
        )}
      </Helmet>
      <div className="min-h-screen bg-slate-50" data-testid="public-website">
        {/* Announcement Bar */}
        {content.landing.announcement_bar?.enabled && showAnnouncement && (
          <div
          className="py-3 px-4 text-center font-medium text-sm relative"
          style={{
            backgroundColor: content.landing.announcement_bar.background_color,
            color: content.landing.announcement_bar.text_color
          }}
        >
          {content.landing.announcement_bar.text}
          <button
            onClick={() => setShowAnnouncement(false)}
            className="absolute right-4 top-1/2 -translate-y-1/2 hover:opacity-70"
          >
            ×
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center" style={{ minHeight: `${Math.max(64, logoSize + 16)}px` }}>
            <div className="flex items-center gap-2 py-2">
              {logo ? (
                <img 
                  src={logo} 
                  alt={companyName} 
                  style={{ height: `${logoSize}px`, maxHeight: `${logoSize}px` }}
                  className="w-auto object-contain" 
                />
              ) : (
                <Package2 className="w-8 h-8 text-emerald-600" />
              )}
              {showCompanyName && (
                <h1 className="text-2xl font-bold text-slate-900">{companyName}</h1>
              )}
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#home" className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">Home</a>
              <a href="#shop" className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">Shop</a>
              <a href="#about" className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">About</a>
              <a href="#contact" className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">Contact</a>
              {user && user.user_type === 'customer' ? (
                <>
                  <a href="/customer-dashboard" className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">Dashboard</a>
                  <button onClick={() => {
                    logout();
                    navigate('/');
                  }} className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">
                    Logout
                  </button>
                </>
              ) : (
                <a href="/login" className="text-slate-700 hover:text-emerald-600 font-medium transition-colors">Login</a>
              )}
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-6 h-6 text-slate-700" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center font-semibold">
                    {cart.length}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200 space-y-2">
              <a href="#home" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">Home</a>
              <a href="#shop" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">Shop</a>
              <a href="#about" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">About</a>
              <a href="#contact" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">Contact</a>
              {user && user.user_type === 'customer' ? (
                <>
                  <a href="/customer-dashboard" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">Dashboard</a>
                  <button onClick={() => {
                    logout();
                    navigate('/');
                  }} className="block w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">
                    Logout
                  </button>
                </>
              ) : (
                <a href="/login" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg">Login</a>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative">
        {isLoadingContent && !content.landing.hero_image && !content.landing.hero_title ? (
          // Loading state - show a neutral background while loading
          <div className="bg-slate-200 animate-pulse py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="h-16 bg-slate-300 rounded-lg mb-6 mx-auto max-w-2xl"></div>
              <div className="h-8 bg-slate-300 rounded-lg mb-8 mx-auto max-w-3xl"></div>
              <div className="h-14 bg-slate-300 rounded-lg mx-auto max-w-xs"></div>
            </div>
          </div>
        ) : content.landing.hero_image ? (
          <div 
            className="relative h-[600px] bg-cover bg-center" 
            style={{ backgroundImage: `url(${content.landing.hero_image})` }}
          >
            <div className="absolute inset-0 bg-transparent"></div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-center text-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                  {content.landing.hero_title || 'Welcome to ASSETCO'}
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white drop-shadow-md max-w-3xl mx-auto">
                  {content.landing.hero_subtitle || 'Your trusted partner for quality products and exceptional service'}
                </p>
                <a href="#shop">
                  <Button className="bg-white text-emerald-600 hover:bg-slate-50 h-14 px-8 text-lg font-semibold shadow-lg">
                    {content.landing.hero_cta || 'Browse Products'}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative bg-gradient-to-br from-emerald-600/90 via-emerald-500/90 to-teal-600/90 text-white py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 drop-shadow-lg">
                {content.landing.hero_title || 'Welcome to ASSETCO'}
              </h1>
              <p className="text-xl lg:text-2xl mb-8 text-white drop-shadow-md max-w-3xl mx-auto">
                {content.landing.hero_subtitle || 'Your trusted partner for quality products and exceptional service'}
              </p>
              <a href="#shop">
                <Button className="bg-white text-emerald-600 hover:bg-slate-50 h-14 px-8 text-lg font-semibold shadow-lg">
                  {content.landing.hero_cta || 'Browse Products'}
                </Button>
              </a>
            </div>
          </div>
        )}
      </section>

      {/* Top Ad Bars */}
      {content.landing.ad_bars?.filter(bar => bar.enabled && bar.position === 'top').map((adBar) => (
        <div key={adBar.id} className="py-8 bg-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Countdown Timer */}
            {adBar.countdown_enabled && countdown[adBar.id] && (
              <div className="mb-6 text-center">
                <div className="inline-flex gap-4 bg-emerald-600 text-white px-8 py-4 rounded-lg shadow-lg">
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].days}</span>
                    <span className="text-xs uppercase">Days</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].hours}</span>
                    <span className="text-xs uppercase">Hours</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].minutes}</span>
                    <span className="text-xs uppercase">Minutes</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].seconds}</span>
                    <span className="text-xs uppercase">Seconds</span>
                  </div>
                </div>
              </div>
            )}

            {/* Carousel or Single Image */}
            {adBar.type === 'image' && adBar.carousel_enabled && adBar.carousel_images?.length > 0 ? (
              <div className="relative">
                <a href={adBar.link || '#'} target={adBar.link ? '_blank' : '_self'} rel="noopener noreferrer">
                  <img 
                    src={adBar.carousel_images[currentSlide[adBar.id] || 0]} 
                    alt={`${adBar.content} - Slide ${(currentSlide[adBar.id] || 0) + 1}`} 
                    className="w-full h-auto rounded-lg transition-opacity duration-500" 
                  />
                  {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
                </a>
                {/* Carousel Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {adBar.carousel_images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(prev => ({ ...prev, [adBar.id]: idx }))}
                      className={`w-3 h-3 rounded-full transition-all ${
                        (currentSlide[adBar.id] || 0) === idx ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            ) : adBar.type === 'image' && adBar.image_url && (
              <a href={adBar.link || '#'} target={adBar.link ? '_blank' : '_self'} rel="noopener noreferrer">
                <img src={adBar.image_url} alt={adBar.content} className="w-full h-auto rounded-lg" />
                {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
              </a>
            )}

            {/* Video */}
            {adBar.type === 'video' && adBar.video_url && (
              <div>
                <iframe
                  src={adBar.video_url}
                  className="w-full h-96 rounded-lg"
                  allowFullScreen
                ></iframe>
                {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
              </div>
            )}

            {/* Text Only */}
            {adBar.type === 'text' && (
              <div className="text-center p-8 bg-white rounded-lg shadow-md">
                <p className="text-2xl font-bold text-slate-900">{adBar.content}</p>
                {adBar.link && (
                  <a href={adBar.link} className="inline-block mt-4 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    Learn More
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Middle Ad Bars */}
      {content.landing.ad_bars?.filter(bar => bar.enabled && bar.position === 'middle').map((adBar) => (
        <div key={adBar.id} className="py-8 bg-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Countdown Timer */}
            {adBar.countdown_enabled && countdown[adBar.id] && (
              <div className="mb-6 text-center">
                <div className="inline-flex gap-4 bg-emerald-600 text-white px-8 py-4 rounded-lg shadow-lg">
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].days}</span>
                    <span className="text-xs uppercase">Days</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].hours}</span>
                    <span className="text-xs uppercase">Hours</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].minutes}</span>
                    <span className="text-xs uppercase">Minutes</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].seconds}</span>
                    <span className="text-xs uppercase">Seconds</span>
                  </div>
                </div>
              </div>
            )}

            {/* Carousel or Single Image */}
            {adBar.type === 'image' && adBar.carousel_enabled && adBar.carousel_images?.length > 0 ? (
              <div className="relative">
                <a href={adBar.link || '#'} target={adBar.link ? '_blank' : '_self'} rel="noopener noreferrer">
                  <img 
                    src={adBar.carousel_images[currentSlide[adBar.id] || 0]} 
                    alt={`${adBar.content} - Slide ${(currentSlide[adBar.id] || 0) + 1}`} 
                    className="w-full h-auto rounded-lg transition-opacity duration-500" 
                  />
                  {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
                </a>
                {/* Carousel Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {adBar.carousel_images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(prev => ({ ...prev, [adBar.id]: idx }))}
                      className={`w-3 h-3 rounded-full transition-all ${
                        (currentSlide[adBar.id] || 0) === idx ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            ) : adBar.type === 'image' && adBar.image_url && (
              <a href={adBar.link || '#'} target={adBar.link ? '_blank' : '_self'} rel="noopener noreferrer">
                <img src={adBar.image_url} alt={adBar.content} className="w-full h-auto rounded-lg" />
                {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
              </a>
            )}

            {/* Video */}
            {adBar.type === 'video' && adBar.video_url && (
              <div>
                <iframe
                  src={adBar.video_url}
                  className="w-full h-96 rounded-lg"
                  allowFullScreen
                ></iframe>
                {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
              </div>
            )}

            {/* Text Only */}
            {adBar.type === 'text' && (
              <div className="text-center p-8 bg-white rounded-lg shadow-md">
                <p className="text-2xl font-bold text-slate-900">{adBar.content}</p>
                {adBar.link && (
                  <a href={adBar.link} className="inline-block mt-4 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    Learn More
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Shop Section */}
      <section id="shop" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Products</h2>
            <p className="text-xl text-slate-600">Browse our collection of quality products</p>
          </div>

          {/* Category Filter */}
          <div className="mb-8">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="max-w-xs mx-auto" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {filteredProducts.map(product => (
              <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer relative" data-testid={`public-product-${product.sku}`}>
                {/* Share Button */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSharingProduct(product);
                    setShowShareDialog(true);
                  }}
                  className="absolute top-2 right-2 z-10 w-8 h-8 p-0 rounded-full bg-white/90 hover:bg-white shadow-md"
                  variant="ghost"
                >
                  <Share2 className="w-4 h-4 text-slate-700" />
                </Button>
                
                <div onClick={() => window.location.href = `/product/${product.id}`}>
                  <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <Package2 className="w-12 h-12 sm:w-20 sm:h-20 text-slate-400" />
                    )}
                  </div>
                  <div className="p-2 sm:p-3 md:p-4 space-y-1 sm:space-y-2 md:space-y-3">
                    <h3 className="font-semibold text-sm sm:text-base md:text-lg text-slate-900 hover:text-emerald-600 transition-colors line-clamp-2">{product.name}</h3>
                    {product.description && (
                      <p className="text-xs sm:text-sm text-slate-600 line-clamp-2 hidden sm:block">{product.description}</p>
                    )}
                    {/* Average Rating */}
                    {productStats[product.id] && productStats[product.id].total_reviews > 0 && (
                      <div className="flex items-center gap-1">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-3 h-3 sm:w-4 sm:h-4 ${
                                star <= Math.round(productStats[product.id].average_rating)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-slate-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs sm:text-sm text-slate-600">
                          {productStats[product.id].average_rating.toFixed(1)} ({productStats[product.id].total_reviews})
                        </span>
                      </div>
                    )}
                    <div className="space-y-2">
                      <p className="text-base sm:text-lg md:text-xl font-bold text-emerald-600">
                        <PriceDisplay usdPrice={product.price} />
                      </p>
                      <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product);
                          }}
                          className="bg-slate-200 hover:bg-slate-300 text-slate-900 flex-1 h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
                          data-testid={`add-to-cart-${product.sku}`}
                        >
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span>Add to Cart</span>
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product);
                            setShowCart(true);
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 h-8 sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
                        >
                          <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          <span>Buy Now</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Package2 className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 text-lg">No products available</p>
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            {content.about.title || 'About Us'}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            {content.about.description || 'We are committed to providing quality products and exceptional service to our customers. With years of experience in the industry, we pride ourselves on our dedication to excellence.'}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-slate-600">We'd love to hear from you</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.contact.email && (
              <Card className="p-6 text-center hover:shadow-lg transition-all">
                <Mail className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
                <p className="text-slate-600">{content.contact.email}</p>
              </Card>
            )}
            {content.contact.phone && (
              <Card className="p-6 text-center hover:shadow-lg transition-all">
                <Phone className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Phone</h3>
                <p className="text-slate-600">{content.contact.phone}</p>
              </Card>
            )}
            {content.contact.address && (
              <Card className="p-6 text-center hover:shadow-lg transition-all">
                <MapPin className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Address</h3>
                <p className="text-slate-600">{content.contact.address}</p>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Bottom Ad Bars */}
      {content.landing.ad_bars?.filter(bar => bar.enabled && bar.position === 'bottom').map((adBar) => (
        <div key={adBar.id} className="py-8 bg-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Countdown Timer */}
            {adBar.countdown_enabled && countdown[adBar.id] && (
              <div className="mb-6 text-center">
                <div className="inline-flex gap-4 bg-emerald-600 text-white px-8 py-4 rounded-lg shadow-lg">
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].days}</span>
                    <span className="text-xs uppercase">Days</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].hours}</span>
                    <span className="text-xs uppercase">Hours</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].minutes}</span>
                    <span className="text-xs uppercase">Minutes</span>
                  </div>
                  <span className="text-3xl font-bold">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl font-bold">{countdown[adBar.id].seconds}</span>
                    <span className="text-xs uppercase">Seconds</span>
                  </div>
                </div>
              </div>
            )}

            {/* Carousel or Single Image */}
            {adBar.type === 'image' && adBar.carousel_enabled && adBar.carousel_images?.length > 0 ? (
              <div className="relative">
                <a href={adBar.link || '#'} target={adBar.link ? '_blank' : '_self'} rel="noopener noreferrer">
                  <img 
                    src={adBar.carousel_images[currentSlide[adBar.id] || 0]} 
                    alt={`${adBar.content} - Slide ${(currentSlide[adBar.id] || 0) + 1}`} 
                    className="w-full h-auto rounded-lg transition-opacity duration-500" 
                  />
                  {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
                </a>
                {/* Carousel Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {adBar.carousel_images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(prev => ({ ...prev, [adBar.id]: idx }))}
                      className={`w-3 h-3 rounded-full transition-all ${
                        (currentSlide[adBar.id] || 0) === idx ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            ) : adBar.type === 'image' && adBar.image_url && (
              <a href={adBar.link || '#'} target={adBar.link ? '_blank' : '_self'} rel="noopener noreferrer">
                <img src={adBar.image_url} alt={adBar.content} className="w-full h-auto rounded-lg" />
                {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
              </a>
            )}

            {/* Video */}
            {adBar.type === 'video' && adBar.video_url && (
              <div>
                <iframe
                  src={adBar.video_url}
                  className="w-full h-96 rounded-lg"
                  allowFullScreen
                ></iframe>
                {adBar.content && <p className="text-center mt-4 text-lg font-medium text-slate-900">{adBar.content}</p>}
              </div>
            )}

            {/* Text Only */}
            {adBar.type === 'text' && (
              <div className="text-center p-8 bg-white rounded-lg shadow-md">
                <p className="text-2xl font-bold text-slate-900">{adBar.content}</p>
                {adBar.link && (
                  <a href={adBar.link} className="inline-block mt-4 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                    Learn More
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Footer */}
      <footer 
        className="py-8"
        style={{ 
          backgroundColor: content.footer?.background_color || '#0f172a',
          color: content.footer?.text_color || '#ffffff'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <p style={{ color: content.footer?.text_color || '#ffffff', opacity: 0.8 }}>
                {content.footer?.copyright_text || '© 2024 ASSETCO. All rights reserved.'}
              </p>
            </div>
            
            {/* Partner Logos */}
            {content.footer?.partner_logos && content.footer.partner_logos.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-4">
                {content.footer.partner_logos.map((logo, index) => (
                  <img 
                    key={index}
                    src={logo} 
                    alt={`Partner ${index + 1}`} 
                    className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cart-dialog">
          <DialogHeader>
            <DialogTitle>Shopping Cart ({cart.length} items)</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {cart.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="w-16 h-16 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600">Your cart is empty</p>
              </div>
            ) : (
              <>
                {cart.map(item => (
                  <div key={item.product_id} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="w-20 h-20 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package2 className="w-10 h-10 text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{item.product.name}</h3>
                      <p className="text-sm text-slate-600">
                        <PriceDisplay usdPrice={item.product.price} /> each
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="h-8 w-8 p-0"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-12 text-center font-medium">{item.quantity}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="h-8 w-8 p-0"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromCart(item.product_id)}
                          className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">
                        <PriceDisplay usdPrice={item.product.price * item.quantity} />
                      </p>
                    </div>
                  </div>
                ))}

                <div className="pt-4 border-t border-slate-200">
                  <div className="flex justify-between text-xl font-bold text-slate-900 mb-6">
                    <span>Total:</span>
                    <span>
                      {customerCurrency === 'MVR' ? (
                        <><CurrencySymbol currency="MVR" width={16} height={16} /> {fmtNum(cartTotal)}</>
                      ) : (
                        `$${fmtNum(cartTotal)}`
                      )}
                    </span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowCart(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleBuyNow}
                      className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                    >
                      Checkout
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delivery Details Dialog */}
      <Dialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delivery Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Delivery Method Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Select Delivery Method *</Label>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  type="button"
                  variant={deliveryMethod === 'landmark' ? 'default' : 'outline'}
                  onClick={() => setDeliveryMethod('landmark')}
                  className="h-20"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Landmark
                </Button>
                <Button
                  type="button"
                  variant={deliveryMethod === 'supply_boat' ? 'default' : 'outline'}
                  onClick={() => setDeliveryMethod('supply_boat')}
                  className="h-20"
                >
                  <Package2 className="w-5 h-5 mr-2" />
                  Supply Boat
                </Button>
              </div>
            </div>

            {/* Landmark Form */}
            {deliveryMethod === 'landmark' && (
              <div className="space-y-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={deliveryDetails.first_name}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, first_name: e.target.value})}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={deliveryDetails.last_name}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, last_name: e.target.value})}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div>
                  <Label>Contact Number *</Label>
                  <Input
                    value={deliveryDetails.contact_number}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, contact_number: e.target.value})}
                    placeholder="7 digits (starts with 7 or 9)"
                    maxLength={7}
                    pattern="[79][0-9]{6}"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Must be 7 digits and start with 7 or 9</p>
                </div>
                <div>
                  <Label>Designation *</Label>
                  <Input
                    value={deliveryDetails.designation}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, designation: e.target.value})}
                    placeholder="Enter designation"
                    required
                  />
                </div>
                <div>
                  <Label>Street Address</Label>
                  <Input
                    value={deliveryDetails.street_address}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, street_address: e.target.value})}
                    placeholder="Enter street address"
                  />
                </div>
                <div>
                  <Label>Building Name</Label>
                  <Input
                    value={deliveryDetails.building_name}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, building_name: e.target.value})}
                    placeholder="Enter building name"
                  />
                </div>
                <div>
                  <Label>Floor</Label>
                  <Input
                    value={deliveryDetails.floor}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, floor: e.target.value})}
                    placeholder="Enter floor number"
                  />
                </div>
                <div>
                  <Label>Google Map Link (Optional)</Label>
                  <Input
                    value={deliveryDetails.google_map_link}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, google_map_link: e.target.value})}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              </div>
            )}

            {/* Supply Boat Form */}
            {deliveryMethod === 'supply_boat' && (
              <div className="space-y-4">
                <div>
                  <Label>Captain Full Name *</Label>
                  <Input
                    value={deliveryDetails.captain_full_name}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, captain_full_name: e.target.value})}
                    placeholder="Enter captain full name"
                    required
                  />
                </div>
                <div>
                  <Label>Captain ID No *</Label>
                  <Input
                    value={deliveryDetails.captain_id_no}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, captain_id_no: e.target.value})}
                    placeholder="Enter captain ID number"
                    required
                  />
                </div>
                <div>
                  <Label>Dhoni Name *</Label>
                  <Input
                    value={deliveryDetails.dhoni_name}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, dhoni_name: e.target.value})}
                    placeholder="Enter dhoni name"
                    required
                  />
                </div>
                <div>
                  <Label>Jetty *</Label>
                  <Input
                    value={deliveryDetails.jetty}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, jetty: e.target.value})}
                    placeholder="Enter jetty location"
                    required
                  />
                </div>
                <div>
                  <Label>Captain Number *</Label>
                  <Input
                    value={deliveryDetails.captain_number}
                    onChange={(e) => setDeliveryDetails({...deliveryDetails, captain_number: e.target.value})}
                    placeholder="7 digits (starts with 7 or 9)"
                    maxLength={7}
                    pattern="[79][0-9]{6}"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Must be 7 digits and start with 7 or 9</p>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeliveryDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={createCustomerOrder}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
              >
                Submit Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Messaging Choice Dialog */}
      <Dialog open={showMessagingChoice} onOpenChange={setShowMessagingChoice}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Choose Messaging Platform</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            <p className="text-slate-600">
              Your order has been created. Please select how you'd like to send it to us:
            </p>
            
            <Button
              onClick={() => placeOrder('whatsapp')}
              className="w-full h-16 bg-green-600 hover:bg-green-700 text-white font-semibold flex items-center justify-center gap-3"
            >
              <Phone className="w-6 h-6" />
              <span className="text-lg">Send via WhatsApp</span>
            </Button>
            
            <Button
              onClick={() => placeOrder('viber')}
              className="w-full h-16 bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center justify-center gap-3"
            >
              <Phone className="w-6 h-6" />
              <span className="text-lg">Send via Viber</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Viber Order Message Dialog */}
      <Dialog open={showViberMessage} onOpenChange={setShowViberMessage}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>📱 Send Order via Viber</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4">
              <p className="font-semibold text-purple-900 mb-3 text-lg">📋 Your Order Message:</p>
              <div className="bg-white p-4 rounded-lg border-2 border-purple-200 max-h-96 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">{orderMessage}</pre>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-semibold text-blue-900 mb-2">📱 How to send:</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-900">
                <li>Click "<strong>Copy & Open Viber</strong>" button below</li>
                <li>Viber will open to <strong>+960 7614441</strong></li>
                <li><strong>Long press</strong> in the message field</li>
                <li>Tap <strong>"Paste"</strong></li>
                <li>Tap <strong>Send</strong> ✓</li>
              </ol>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={openViberWithOrder}
                className="flex-1 h-16 bg-purple-600 hover:bg-purple-700 text-white text-lg font-semibold"
              >
                <Phone className="w-6 h-6 mr-2" />
                Copy & Open Viber
              </Button>
              <Button
                onClick={() => {
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(orderMessage);
                    toast.success('Message copied to clipboard!');
                  }
                }}
                variant="outline"
                className="h-16 px-8"
              >
                📋 Copy Only
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inquiry Form Dialog */}
      <Dialog open={showInquiryForm} onOpenChange={setShowInquiryForm}>
        <DialogContent className="max-w-md" data-testid="inquiry-form-dialog">
          <DialogHeader>
            <DialogTitle>Submit Inquiry</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={submitInquiry} className="space-y-4">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={inquiryForm.customer_name}
                onChange={(e) => setInquiryForm({ ...inquiryForm, customer_name: e.target.value })}
                required
                data-testid="inquiry-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={inquiryForm.customer_email}
                onChange={(e) => setInquiryForm({ ...inquiryForm, customer_email: e.target.value })}
                required
                data-testid="inquiry-email-input"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={inquiryForm.customer_phone}
                onChange={(e) => setInquiryForm({ ...inquiryForm, customer_phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Message</Label>
              <Input
                value={inquiryForm.message}
                onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                placeholder="Any special requirements?"
              />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Cart Summary:</p>
              <p className="font-semibold text-slate-900">{cart.length} items - {customerCurrency === 'MVR' ? `${fmtNum(cartTotal)} ރ.` : `$${fmtNum(cartTotal)}`}</p>
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 h-12" data-testid="submit-inquiry-button">
              <Send className="w-4 h-4 mr-2" />
              Submit Inquiry
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share Product Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Product</DialogTitle>
          </DialogHeader>
          
          {sharingProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-900">{sharingProduct.name}</p>
                <p className="text-sm text-emerald-600">{formatPrice(sharingProduct.price)}</p>
              </div>
              
              <div className="space-y-2">
                <Button
                  onClick={() => handleShare(sharingProduct, 'whatsapp')}
                  className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Share via WhatsApp
                </Button>
                
                <Button
                  onClick={() => handleShare(sharingProduct, 'viber')}
                  className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Share via Viber
                </Button>
                
                <Button
                  onClick={() => handleShare(sharingProduct, 'copy')}
                  className="w-full h-12 bg-slate-600 hover:bg-slate-700 text-white"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}
